import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useFeatureFlags } from '@node-space/hooks'
import { Modal, ModalDivider } from '@node-space/storybook-components/dist/Modal'
import Tabs from '@node-space/storybook-components/dist/Tabs'
import useConvertAction from 'pages/Wallets/hooks/useConvertAction'
import useDepositAction from 'pages/Wallets/hooks/useDepositAction'
import usePayAction from 'pages/Wallets/hooks/usePayAction'
import useReceiveAction from 'pages/Wallets/hooks/useReceiveAction'
import useSendAction from 'pages/Wallets/hooks/useSendAction'
import useWithdrawAction from 'pages/Wallets/hooks/useWithdrawAction'
import { TableSectionItem } from '../../types'
import { Convert } from '../Convert/Convert'
import { Deposit } from '../Deposit/Deposit'
import { Pay } from '../Pay/Pay'
import { Receive } from '../Receive/Receive'
import { Send } from '../Send/Send'
import { Withdraw } from '../Withdraw/Withdraw'
import { WithdrawV2 } from '../Withdraw/WithdrawV2'

export type WalletActionKey =
  | 'pay'
  | 'withdraw'
  | 'deposit'
  | 'send'
  | 'receive'
  | 'convert'
  | undefined

export type WalletAction = { key: WalletActionKey; data?: TableSectionItem | { id?: string } }

export type WalletActionSharedProps = {
  walletId: string
  actionWalletId: string
  setWalletId: Dispatch<SetStateAction<string>>
  onModalClose: () => void
}

type WalletTab = {
  id: WalletActionKey
  title: string
  visible?: boolean
}

interface ModalProps {
  action: WalletAction
  supportedFiatActions?: {
    supportsDeposits: boolean
    supportsWithdrawals: boolean
  }
  setWalletModalAction?: Dispatch<SetStateAction<WalletAction>>
  refresh: () => void
  setShowWalletModal?: Dispatch<SetStateAction<boolean>>
}

export interface WalletTabProps {
  setTabVisible?: Dispatch<SetStateAction<boolean>>
  action: WalletAction
  walletId?: string
  setWalletId?: Dispatch<SetStateAction<string>>
  refresh: () => void
  step?: string
}

const cryptoTabs: WalletTab[] = [
  { id: 'convert', title: 'Convert' },
  { id: 'send', title: 'Send' },
  { id: 'receive', title: 'Receive' },
]

const WalletModal = ({
  action,
  supportedFiatActions,
  setWalletModalAction,
  refresh,
  setShowWalletModal,
}: ModalProps) => {
  const { enableUseMigratedPaymentMethods } = useFeatureFlags()
  const [tabs, setTabs] = useState<WalletTab[]>([])
  const [activeTab, setTab] = useState<WalletActionKey>(undefined)
  const [tabVisible, setTabVisible] = useState(true)
  // shared wallet id to persist selected wallet state across tabs
  const [walletId, setWalletId] = useState<string>()

  const actionWalletId = action?.data?.id?.toString()
  const isSingleTab = tabs.filter(x => x.visible !== false)?.length === 1

  useEffect(() => {
    const { supportsWithdrawals, supportsDeposits } = supportedFiatActions || {}
    const fiatTabs = [
      { id: 'pay', title: 'Pay', visible: false },
      { id: 'withdraw', title: 'Withdraw', visible: supportsWithdrawals },
      { id: 'deposit', title: 'Deposit', visible: supportsDeposits },
    ] as WalletTab[]
    const isFiatAction =
      action?.key === 'pay' || action?.key === 'withdraw' || action?.key === 'deposit'
    const enabledTabs = isFiatAction ? fiatTabs : cryptoTabs

    setTabs(enabledTabs)
  }, [action, supportedFiatActions])

  useEffect(() => {
    setActiveTab(tabs.find(x => x.id === action?.key)?.id)
  }, [action, tabs])

  const setActiveTab = (t: string) => {
    const tab = t as WalletActionKey
    setTab(tab)
  }

  // clear state on modal close
  const onModalClose = () => {
    setWalletModalAction(undefined)
    setActiveTab(undefined)
    setWalletId(undefined)
    setShowWalletModal?.(false)
  }
  const sharedActionProps: WalletActionSharedProps = {
    walletId,
    actionWalletId,
    setWalletId,
    onModalClose,
  }

  const withdrawAction = useWithdrawAction(sharedActionProps)
  const depositAction = useDepositAction(sharedActionProps)
  const convertAction = useConvertAction(sharedActionProps)
  const sendAction = useSendAction(sharedActionProps)
  const receiveAction = useReceiveAction(sharedActionProps)
  const payAction = usePayAction({ sharedActionProps })

  // renders first step of wallet action based on active tab
  // e.g. 'pay' | 'withdraw' | 'deposit' | 'convert' | 'send' | 'receive'
  const renderWalletTab = (_action: WalletAction) => {
    const props: WalletTabProps = {
      setTabVisible,
      action: _action,
      walletId,
      setWalletId,
      refresh,
    }

    switch (activeTab) {
      case 'pay':
        return (
          <Pay
            {...{
              ...props,
              ...payAction,
              isLimitsError: false,
            }}
          />
        )
      case 'withdraw':
        return enableUseMigratedPaymentMethods ? (
          <WithdrawV2
            {...{
              ...props,
              ...withdrawAction,
            }}
          />
        ) : (
          <Withdraw
            {...{
              ...props,
              ...withdrawAction,
            }}
          />
        )
      case 'deposit':
        return (
          <Deposit
            {...{
              ...props,
              ...depositAction,
            }}
          />
        )
      case 'send':
        return (
          <Send
            {...{
              ...props,
              ...sendAction,
            }}
          />
        )
      case 'receive':
        return (
          <Receive
            {...{
              ...props,
              ...receiveAction,
            }}
          />
        )
      case 'convert':
        return (
          <Convert
            {...{
              ...props,
              ...convertAction,
            }}
          />
        )
    }
  }

  const isCryptoWalletAction = ['send', 'receive', 'convert'].includes(action?.key)

  return activeTab ? (
    <Modal visible closeModal={() => onModalClose()}>
      <>
        {/* modal tabs */}
        {tabVisible && !isSingleTab && isCryptoWalletAction && (
          <div>
            <Tabs
              tabs={tabs.filter(x => x.visible !== false)}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            <ModalDivider />
          </div>
        )}

        {/* modal details */}
        {renderWalletTab(action)}
      </>
    </Modal>
  ) : null
}

export default WalletModal
